// The browser WebAuthn REGISTRATION ceremony as a service (the browser-as-services rule):
// `navigator.credentials.create` + the base64url <-> ArrayBuffer wire conversions live here, so
// the MfaStore stays a plain testable object and tests inject `FakeWebauthnCeremony`.

import { token } from '@/infrastructure/container';
import { DomainError } from '@/domain/errors';

/** The user cancelled / timed out the authenticator prompt (browser `NotAllowedError`). */
export class CeremonyCancelled extends DomainError {
  readonly tag = 'CeremonyCancelled' as const;
  constructor() {
    super('the security key prompt was cancelled or timed out');
  }
}

export interface IWebauthnCeremony {
  /** Whether this browser can run WebAuthn at all (the section hides itself when not). */
  supported(): boolean;
  /** Run `navigator.credentials.create` for the server's creation challenge (the `ccr` from
   * webauthn start) and return the attestation in the WebAuthn JSON wire shape the finish
   * endpoint deserializes. Throws `CeremonyCancelled` on a user-cancelled prompt. */
  create(ccr: unknown): Promise<unknown>;
}

export const TOKEN_IWebauthnCeremony = token<IWebauthnCeremony>('IWebauthnCeremony');

const toBytes = (s: string): Uint8Array => {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.length % 4 ? b64 + '='.repeat(4 - (b64.length % 4)) : b64;
  return Uint8Array.from(atob(padded), (c) => c.charCodeAt(0));
};
const toB64u = (buf: ArrayBuffer): string => {
  // Byte-by-byte, never a spread: a full packed attestation can be kilobytes, and spreading every
  // byte as a function argument risks a RangeError on real hardware keys (the virtual-authenticator
  // path only ever produces small none-fmt attestations, so a test cannot catch it).
  let bin = '';
  for (const b of new Uint8Array(buf)) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

// The wire CCR as far as this service needs to see it (binary fields ride as base64url strings).
interface WireCcr {
  publicKey: {
    challenge: string;
    user: { id: string; [k: string]: unknown };
    excludeCredentials?: { id: string; [k: string]: unknown }[];
    [k: string]: unknown;
  };
}

export class WebauthnCeremony implements IWebauthnCeremony {
  supported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.credentials && 'PublicKeyCredential' in window;
  }

  async create(ccr: unknown): Promise<unknown> {
    const wire = ccr as WireCcr;
    // Decode the binary fields in place; everything else passes through untouched.
    const publicKey = {
      ...wire.publicKey,
      challenge: toBytes(wire.publicKey.challenge),
      user: { ...wire.publicKey.user, id: toBytes(wire.publicKey.user.id) },
      excludeCredentials: wire.publicKey.excludeCredentials?.map((c) => ({ ...c, id: toBytes(c.id) })),
    } as unknown as PublicKeyCredentialCreationOptions;
    let cred: Credential | null;
    try {
      cred = await navigator.credentials.create({ publicKey });
    } catch (e) {
      if (e instanceof DOMException && e.name === 'NotAllowedError') throw new CeremonyCancelled();
      throw e;
    }
    if (!cred) throw new CeremonyCancelled();
    const pk = cred as PublicKeyCredential;
    const resp = pk.response as AuthenticatorAttestationResponse;
    return {
      id: pk.id,
      rawId: toB64u(pk.rawId),
      type: pk.type,
      extensions: pk.getClientExtensionResults ? pk.getClientExtensionResults() : {},
      response: {
        attestationObject: toB64u(resp.attestationObject),
        clientDataJSON: toB64u(resp.clientDataJSON),
      },
    };
  }
}

/** Test fake: records the challenge it was handed and returns a canned attestation. */
export class FakeWebauthnCeremony implements IWebauthnCeremony {
  isSupported = true;
  createCalls: unknown[] = [];
  failNext: Error | null = null;
  attestation: unknown = { id: 'fake-cred', type: 'public-key' };

  supported(): boolean {
    return this.isSupported;
  }
  async create(ccr: unknown): Promise<unknown> {
    if (this.failNext) {
      const e = this.failNext;
      this.failNext = null;
      throw e;
    }
    this.createCalls.push(ccr);
    return this.attestation;
  }
}
